import { formatInTimeZone } from "date-fns-tz";
import { CheckCircle, XCircle } from "lucide-react";
import { useMemo } from "react";

import { MigrationLog } from "@/client";
import { ErrorElement } from "@/components/error-element";
import { LoadingScreen } from "@/components/loading-screen";
import { useTableControl } from "@/components/table-control";
import { DataTable } from "@/features/abstracts-sections/components/data-table";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { useMigrationLogs } from "@/features/migration-logs/api/get-migration-logs";
import { pageTitle } from "@/utils/page-title";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

export function MigrationLogsPage() {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<MigrationLog>();
  const { congress } = useCongress();
  const { pagination, setPagination } = useTableControl();
  const {
    data: migrationLogs,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSuccess,
  } = useMigrationLogs(congress.id);
  const columns = useMemo(
    () => [
      columnHelper.accessor("createdAt", {
        id: "createdAt",
        cell: (info) => {
          const createdAt = info.getValue();
          if (!createdAt) return "-";

          return formatInTimeZone(
            new Date(createdAt + "Z"),
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            "yyyy.MM.dd HH:mm",
          );
        },
        header: () => <span>{t("table.createdAt")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("isSuccess", {
        id: "isSuccess",
        cell: (info) => {
          const isSuccess = info.getValue();
          if (isSuccess) return <CheckCircle className="text-green-500" />;
          return <XCircle className="text-red-500" />;
        },
        header: () => <span>{t("table.status")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("error", {
        id: "error",
        cell: (info) => {
          const error = info.getValue();
          if (!error) return "-";
          return error;
        },
        header: () => <span>{t("common.error")}</span>,
        enableSorting: false,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper, t],
  );

  if (isLoading) return <LoadingScreen />;

  if (!isSuccess) {
    return <ErrorElement />;
  }

  return (
    <div>
      <title>{pageTitle(t("meta.migrationLogs"))}</title>
      <DataTable
        columns={columns}
        data={migrationLogs}
        pagination={pagination}
        pageCount={1}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </div>
  );
}
